import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { cn } from '../../lib/utils'
import { businessTime } from '../../lib/businessTime'

// The customer page's frame (BRIEF-E2b; docs/design/customer-page/). Navy
// header with the brass mark, the section links, and the person signed in.
// Every section of the page renders what its /soc/ route said and nothing the
// browser worked out on its own.

export type SocNavKey = 'today' | 'tripwires' | 'questions' | 'world' | 'concierge' | 'reach'

// BRIEF-E5d: every /client page wears this one header, and its menu reaches
// every customer page from anywhere. The old icon-only header (bell, shield,
// "First meeting") is gone.
export const SOC_MENU: { key: SocNavKey; label: string; to: string }[] = [
  { key: 'today', label: 'Today', to: '/client' },
  { key: 'tripwires', label: 'Tripwires', to: '/client/tripwires' },
  { key: 'questions', label: 'Questions', to: '/client/questions' },
  { key: 'world', label: 'Your world', to: '/client/world' },
  { key: 'concierge', label: 'Talk to your SOC', to: '/client/concierge' },
  { key: 'reach', label: 'How we reach you', to: '/client/settings/escalation' },
]

export function SocMark({ size = 30 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 30 30"
      fill="none"
      stroke="#c49a5a"
      strokeWidth="1.8"
    >
      <circle cx="15" cy="15" r="12" />
      <path d="M15 7v8l5 3" />
    </svg>
  )
}

function navClass(on: boolean) {
  return cn(
    'inline-flex min-h-[44px] items-center no-underline hover:text-white',
    on ? 'text-white border-b-2 border-brass-light' : 'text-[#d9cdb8]',
  )
}

export function SocShell({
  current,
  held = false,
  mainClassName,
  children,
}: {
  current?: SocNavKey
  /** A first meeting is owed: the menu is held back (its pages would turn
   *  the customer away until the meeting is sat); the brand and sign out stay. */
  held?: boolean
  mainClassName?: string
  children: ReactNode
}) {
  const principal = useAuthStore((s) => s.principal)
  const logout = useAuthStore((s) => s.logout)
  return (
    <div data-testid="soc-shell" className="min-h-screen flex flex-col bg-cream font-sans text-navy">
      <header className="bg-navy text-cream">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center gap-x-8 gap-y-2 px-4 py-3 sm:px-6 sm:py-4">
          <Link to="/client" className="flex shrink-0 items-center gap-3 text-cream no-underline">
            <SocMark />
            <span className="font-serif text-2xl tracking-wide">DigitalWatch</span>
          </Link>
          {!held && (
            <nav
              aria-label="Sections"
              className="order-last flex basis-full flex-wrap gap-x-5 gap-y-0 text-[15px] lg:order-none lg:basis-auto lg:grow"
            >
              {SOC_MENU.filter((item) => !(item.key === 'tripwires' && principal?.plain_member)).map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  aria-current={current === item.key ? 'page' : undefined}
                  className={navClass(current === item.key)}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          )}
          <div className="ml-auto flex items-center gap-4 text-sm text-[#d9cdb8]">
            {principal?.display_name && <span className="hidden sm:inline">{principal.display_name}</span>}
            <button
              type="button"
              onClick={logout}
              className="min-h-[44px] cursor-pointer text-[#d9cdb8] hover:text-white"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main
        className={
          mainClassName ??
          'mx-auto box-border flex w-full max-w-[1200px] flex-col gap-9 px-4 pb-16 pt-9 sm:px-6'
        }
      >
        {children}
      </main>
      <footer className="border-t border-[#e6dfd2] px-6 py-4 text-center text-[13px] text-[#6b5a3e]">
        DigitalWatch by OSELAB · a SOC for small business and family offices
      </footer>
    </div>
  )
}

// ── Shared pieces ───────────────────────────────────────────────────

export const kicker = 'text-xs uppercase tracking-[.14em] text-[#6b5a3e]'
export const card = 'rounded-[10px] border border-[#e6dfd2] bg-white'
export const h2Class = 'font-serif text-[28px] font-normal leading-tight m-0'
export const btn =
  'min-h-[44px] cursor-pointer rounded-md border border-navy bg-navy px-[18px] py-[11px] text-[15px] font-bold text-cream disabled:opacity-60'
export const btnGhost =
  'min-h-[44px] cursor-pointer rounded-md border border-navy bg-transparent px-[18px] py-[11px] text-[15px] font-bold text-navy disabled:opacity-60'
export const btnBrass =
  'min-h-[44px] cursor-pointer rounded-md border border-brass bg-brass px-[18px] py-[11px] text-[15px] font-bold text-cream disabled:opacity-60'
export const linkClass = 'font-bold text-[#8a6830] hover:text-navy'

/** A route's `could_not_see` list, said as one sentence. Nothing when the
 *  route sent none: the route decides what was not seen, not this page. */
export function CouldNotSee({
  items,
  className,
}: {
  items: string[] | null | undefined
  className?: string
}) {
  const lines = (items ?? [])
    .map((line) => String(line).trim().replace(/[.;]+$/, ''))
    .filter(Boolean)
  if (lines.length === 0) return null
  return (
    <p data-testid="could-not-see" className={cn('m-0 text-sm text-[#6b5a3e]', className)}>
      What we could not see: {lines.join('; ')}.
    </p>
  )
}

/** A route timestamp in the business's own clock (BRIEF-E5d), or null when
 *  there is none. */
export function when(iso: string | null | undefined, pattern = 'EEE d MMM, HH:mm'): string | null {
  return businessTime(iso, pattern)
}

/** The detail a route refused with, else a plain sentence of our own. */
export function routeError(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' && detail.trim() ? detail : fallback
}

export function routeStatus(err: unknown): number | null {
  const s = (err as { response?: { status?: unknown } })?.response?.status
  return typeof s === 'number' ? s : null
}
